import React from 'react';
import { SearchIcon } from 'lucide-react';
import { useAcademy } from '../../../context/Academy';
import { useAcademyHub } from '../../../context/AcademyHub';
import { SAMPLE_ROSTER } from '../../../data/academyAdmin';
import { Button } from '../../ui/Button';
import { inputClass } from '../../ui/Field';
import { initials, timeAgo } from '../../../utils/academyFormat';
interface Recipient {
  name: string;
  email: string;
  detail: string;
  courses: string[];
}

/** One-to-one messages from staff to individual learners. */
export function AdminMessages() {
  const {
    state
  } = useAcademy();
  const {
    hub,
    courses,
    messageLearner,
    markConversationRead
  } = useAcademyHub();
  const [query, setQuery] = React.useState('');
  const [selected, setSelected] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState('');
  const listRef = React.useRef<HTMLOListElement>(null);
  const titleOf = (slug: string) => courses.find(c => c.slug === slug)?.title ?? slug;
  const recipients: Recipient[] = React.useMemo(() => {
    const list: Recipient[] = SAMPLE_ROSTER.filter(l => l.role === 'learner').map(l => ({
      name: l.name,
      email: l.email,
      detail: `${l.organisation}, ${l.country}`,
      courses: Object.keys(l.enrolled)
    }));
    // The learner signed in on this browser, so a message can be checked end to end.
    if (state.learner && !list.some(r => r.email === state.learner?.email)) {
      list.unshift({
        name: state.learner.name,
        email: state.learner.email,
        detail: `${[state.learner.organisation, state.learner.country].filter(Boolean).join(', ')} · signed in on this browser`,
        courses: Object.keys(state.enrolments)
      });
    }
    // Learners who wrote in but are not on the roster.
    for (const m of hub.messages) {
      if (!list.some(r => r.email === m.learnerEmail)) list.push({
        name: m.learnerName,
        email: m.learnerEmail,
        detail: 'Learner',
        courses: []
      });
    }
    return list;
  }, [state.learner, state.enrolments, hub.messages]);
  const lastOf = (email: string) => hub.messages.filter(m => m.learnerEmail === email).slice(-1)[0];
  const lastAt = (email: string) => lastOf(email)?.at ?? '';
  const unread = (email: string) => hub.messages.filter(m => m.learnerEmail === email && m.from === 'learner' && !m.read).length;
  const q = query.trim().toLowerCase();
  const shown = recipients.filter(r => !q || r.name.toLowerCase().includes(q) || r.email.includes(q) || r.detail.toLowerCase().includes(q)).sort((a, b) => lastAt(b.email).localeCompare(lastAt(a.email)));
  const current = recipients.find(r => r.email === selected) ?? null;
  const thread = current ? hub.messages.filter(m => m.learnerEmail === current.email) : [];
  const currentEmail = current?.email;
  React.useEffect(() => {
    if (currentEmail) markConversationRead(currentEmail, 'staff');
  }, [currentEmail, thread.length, markConversationRead]);
  React.useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight
    });
  }, [thread.length, selected]);
  const send = (event: React.FormEvent) => {
    event.preventDefault();
    if (!current || !draft.trim()) return;
    messageLearner({
      email: current.email,
      name: current.name
    }, draft);
    setDraft('');
  };
  return <div className="grid min-h-[520px] overflow-hidden rounded-md border border-line lg:grid-cols-[320px_1fr]" data-id="element-194" data-mp="1xn3vxrfqzdcu">
      <div className={`border-line lg:border-r ${current ? 'hidden lg:block' : ''}`} data-id="element-195" data-mp="1ipl7yraw946g">
        <div className="border-b border-line p-3" data-id="element-196" data-mp="1noxvzfdi4ham">
          <div className="relative" data-id="element-197" data-mp="1nqr1usgaixhk">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-198" data-mp="19pep0pwffbzw" />
            <label htmlFor="learner-search" className="sr-only" data-id="element-199" data-mp="1epj6oj92mcrp">
              Find a learner
            </label>
            <input id="learner-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Find a learner" className={`${inputClass} py-2 pl-9 text-[14px]`} data-id="element-200" data-mp="344qztxvksd26" />
          </div>
        </div>
        <ul className="max-h-[470px] overflow-y-auto" data-id="element-201" data-mp="141zjulw6ehy4">
          {shown.map(r => {
          const n = unread(r.email);
          const last = lastOf(r.email);
          return <li key={r.email} data-id="element-202" data-mp="uzyndr8rnzxi">
                <button type="button" onClick={() => setSelected(r.email)} aria-current={selected === r.email ? 'true' : undefined} className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors duration-150 ${selected === r.email ? 'bg-panel' : 'hover:bg-panel'}`} data-id="element-203" data-mp="v2upzsvk7csa">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-charcoal text-[12px] font-medium text-ink" aria-hidden="true" data-id="element-204" data-mp="7anusx1klbug">
                    {initials(r.name)}
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-205" data-mp="2dy4qh7oejc09">
                    <span className="flex items-center justify-between gap-2" data-id="element-206" data-mp="2e1iq485ol740">
                      <span className={`truncate text-[14px] ${n ? 'font-medium text-ink' : 'text-ink'}`} data-id="element-207" data-mp="2iwg29ny9bwl4">{r.name}</span>
                      {n > 0 && <span className="rounded-full bg-gold px-1.5 text-[11px] font-semibold text-charcoal" data-id="element-208" data-mp="2ryp8w2qen5t8">{n}</span>}
                    </span>
                    <span className="block truncate text-small text-muted" data-id="element-209" data-mp="1v9zoo5rb2143">{last ? last.body : r.detail}</span>
                  </span>
                </button>
              </li>;
        })}
          {!shown.length && <li className="px-4 py-8 text-center text-[14px] text-muted" data-id="element-210" data-mp="2481zte4r6a53">No learners match.</li>}
        </ul>
      </div>

      {current ? <div className="flex min-w-0 flex-col" data-id="element-211" data-mp="1bkz0ijs95vds">
          <div className="border-b border-line px-5 py-4" data-id="element-212" data-mp="1bk6349f6oi8b">
            <button type="button" onClick={() => setSelected(null)} className="mb-2 text-small text-royal hover:text-ink lg:hidden" data-id="element-213" data-mp="1gjag7b58b03x">
              All learners
            </button>
            <p className="text-[15px] font-medium text-ink" data-id="element-214" data-mp="2evv1sw4fxbj9">{current.name}</p>
            <p className="text-small text-muted" data-id="element-215" data-mp="2etijuiyon167">
              {current.email} · {current.detail}
            </p>
            {current.courses.length > 0 && <p className="mt-1 truncate text-small text-body" data-id="element-216" data-mp="2omppm4xc4j6t">Enrolled: {current.courses.map(titleOf).join(', ')}</p>}
          </div>
          <ol ref={listRef} aria-live="polite" className="flex-1 space-y-3 overflow-y-auto px-5 py-4" style={{
        maxHeight: 360
      }} data-id="element-217" data-mp="1vwqupswglru8">
            {thread.length ? thread.map(m => <li key={m.id} className={`flex ${m.from === 'staff' ? 'justify-end' : 'justify-start'}`} data-id="element-218" data-mp="24xf9wqxl9y8p">
                  <div className={`max-w-[80%] rounded-md px-3.5 py-2.5 text-[14px] ${m.from === 'staff' ? 'bg-gold text-charcoal' : 'bg-panel text-ink'}`} data-id="element-219" data-mp="3lbf6pt1517rq">
                    <p className="whitespace-pre-line" data-id="element-220" data-mp="3gc2ojll7oovh">{m.body}</p>
                    <p className={`mt-1 text-[11px] ${m.from === 'staff' ? 'text-charcoal/70' : 'text-muted'}`} data-id="element-221" data-mp="3otcx3zl9zjhk">
                      {m.senderName} · {timeAgo(m.at)}
                    </p>
                  </div>
                </li>) : <li className="py-10 text-center text-[14px] text-muted" data-id="element-222" data-mp="3jui98j8gb4ql">
                No messages yet. {current.name.split(' ')[0]} gets an Academy notification when you write.
              </li>}
          </ol>
          <form onSubmit={send} className="flex gap-2 border-t border-line p-3" data-id="element-223" data-mp="ge2os9qsli1c">
            <label htmlFor="dm-draft" className="sr-only" data-id="element-224" data-mp="1p6iah4dwd2u">
              Message {current.name}
            </label>
            <textarea id="dm-draft" rows={2} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) send(e);
        }} placeholder={`Message ${current.name.split(' ')[0]}`} className={`${inputClass} resize-none py-2 text-[14px]`} data-id="element-225" data-mp="6n83b7ldemgy" />
            <Button type="submit" disabled={!draft.trim()} className="self-end" data-id="element-226" data-mp="2qfxev9gde61u">
              Send
            </Button>
          </form>
        </div> : <div className="hidden items-center justify-center px-8 text-center text-[14px] text-muted lg:flex" data-id="element-227" data-mp="2hgkdz56bapsq">
          Choose a learner to start or continue a conversation.
        </div>}
    </div>;
}